import React from 'react';
import { Form } from '../../../ui/universe';
import {
  transEdu,
  getQuestionBoxes,
  buildEduQuestionKey,
} from '../../../../utils/index';
import SafeMathJax from '../../../ui/SafeMathJax';
import { useCollapsibleList } from '../../../../hooks';

const TypeMap = {
  'Question': '题干',
  'Option': '选项',
  'Figure': '插图',
  'Table': '表格',
  'Answer': '答案',
  'Parse': '解析',
};

const GroupTypeMap = {
  'multiple-choice': '选择题',
  'fill-in-the-blank': '填空题',
  'problem-solving': '解答题',
  'arithmetic': '算术题'
};

/**
 * 折叠图标组件
 */
const CollapseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.77971 9L7.00005 6.33766L4.22039 9L3.0001 7.83117L7.00005 4L11 7.83117L9.77971 9Z" fill="#32394B" />
  </svg>
);

/**
 * 单页的题目列表（递归内部使用）
 * 单页时 pagePrefix 为空串，保持旧 key 兼容同步接口
 * 多页时 pagePrefix = `_p${pageNum}_`（与 rect 生成侧一致）
 */
function EduPageBlock({
  resultList,
  dep = 1,
  onSelect,
  selectRect,
  pid = '',
  pagePrefix = '',
  pageNum = 1,
}) {
  const { isCollapsed, toggleItem } = useCollapsibleList();

  // key 生成器：与 utils/rect.js 的 buildEduQuestionKey 保持一致
  //   多页模式：Question_p${page}_${pid}${questionId}${dep}_c0
  //             result_p${detail.PageIndex||page}_${pid}${questionId}${type}${Index}${dep}
  //   单页模式：Question${pid}${questionId}${dep}（多坐标框时带 _c0/_c1）
  //             result${pid}${questionId}${type}${Index}${dep}
  const buildResultKey = (questionId, detail, effectivePagePrefix) =>
    effectivePagePrefix
      ? `result${effectivePagePrefix}${pid}${questionId}${detail.type}${detail.Index}${dep}`
      : `result${pid}${questionId}${detail.type}${detail.Index}${dep}`;

  return (
    <div className="edu_container">
      {resultList?.length > 0 && resultList.map((question, questionId) => {
        const params = transEdu(question);
        // 跨栏/跨页判定：Coord 多个坐标框，CoordPageIndex 相同为跨栏、不同为跨页
        const { type: crossType, boxes } = getQuestionBoxes(question);
        const questionKey = buildEduQuestionKey({
          isMultiPage: !!pagePrefix,
          pageNum,
          pId: pid,
          questionId,
          dep,
          boxIndex: 0,
          hasMultiBox: boxes.length > 1,
        });

        return (
          <div
            key={questionId}
            style={{ marginLeft: `${dep * 16}px` }}
            className={`${dep > 1 ? 'isSub' : ''} ${isCollapsed(questionId) ? 'isHide' : 'isActive'} ${questionKey === selectRect ? 'isChecked' : ''} edu_container__item`}
          >
            <div
              className="edu_title_container"
              onClick={() => {
                onSelect && onSelect(questionKey);
              }}
            >
              <span className="edu_title__tips"></span>
              <span className="edu_title__text">
                {dep > 1 ? '子' : ''}题目{questionId + 1}
              </span>
              {crossType && (
                <span className="edu_title__crossTag">{crossType}</span>
              )}
              <span
                className="edu_title__collaps"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleItem(questionId);
                }}
              >
                <CollapseIcon />
              </span>
            </div>

            <div className="edu_body_container">
              {params.map((detail, detailIdx) => {
                // 子片段自身可能带 PageIndex，跨页片段点击时应定位到自己所在的页
                const detailPagePrefix = pagePrefix && detail.PageIndex
                  ? `_p${detail.PageIndex}_`
                  : pagePrefix;
                const resultKey = buildResultKey(questionId, detail, detailPagePrefix);

                return (
                  <React.Fragment key={detailIdx}>
                    {detail?.GroupType && detail.type === 'Question' && (
                      <Form.Item label="题目类型">
                        {GroupTypeMap[detail?.GroupType]}
                      </Form.Item>
                    )}

                    {TypeMap[detail.type] && (
                      <Form.Item
                        className={`${resultKey === selectRect ? 'isChecked' : ''}`}
                        label={detail.type === 'Figure' ? `${TypeMap[detail.type]}${detail.dIdx + 1}` : TypeMap[detail.type]}
                        onClick={() => {
                          onSelect && onSelect(resultKey);
                        }}
                      >
                        <div className="edu_form__item">
                          {detail.type === 'Figure' && (
                            <span className="edu_form__item--figure">
                              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="31" height="31" rx="3.5" fill="#F4F5F7" />
                                <rect x="0.5" y="0.5" width="31" height="31" rx="3.5" stroke="#E0E2E7" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M20.1005 13.4054C20.8136 13.4054 21.3917 12.8246 21.3917 12.1081C21.3917 11.3916 20.8136 10.8108 20.1005 10.8108C19.3874 10.8108 18.8094 11.3916 18.8094 12.1081C18.8094 12.8246 19.3874 13.4054 20.1005 13.4054ZM13.1789 13.2864C13.3504 13.0328 13.7238 13.0328 13.8953 13.2864L17.6414 18.8257L19.6862 15.8597C19.8566 15.6126 20.2207 15.6099 20.3946 15.8545L23.7033 20.5061C23.907 20.7924 23.7023 21.1892 23.3509 21.1892H18.4253H16.0119H8.64899C8.30231 21.1892 8.09657 20.8017 8.29078 20.5145L13.1789 13.2864Z" fill="#CDD0D9" />
                              </svg>
                            </span>
                          )}
                          {detail?.Text && (
                            <SafeMathJax>
                              {detail.Text}
                            </SafeMathJax>
                          )}
                        </div>
                      </Form.Item>
                    )}

                    {detail.ResultList?.length > 0 && (
                      <EduPageBlock
                        selectRect={selectRect}
                        onSelect={onSelect}
                        resultList={detail.ResultList}
                        dep={dep + 1}
                        pid={questionId}
                        pagePrefix={pagePrefix}
                        pageNum={pageNum}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Edu 渲染器 - 教育题目识别结果
 * 多页支持：questionInfo 长度 > 1 时按页分节渲染，每页显示"第 N 页"分节标题
 *   题目/子项点击时 emit 的 key 会带上 `_p{pageNum}_` 前缀，与 rect 侧对齐
 * 单页兼容：questionInfo 长度 === 1 时保持旧 key 格式，同步接口联动不变
 *
 * @param {Object} props
 * @param {Array} props.questionInfo - QuestionInfo 数组
 * @param {Function} props.onSelect - 选中回调
 * @param {string} props.selectRect - 当前选中的矩形 key
 */
function EduRenderer({ questionInfo = [], onSelect, selectRect }) {
  const isMultiPage = questionInfo.length > 1;

  if (!isMultiPage) {
    // 单页：保持原有 UI 和 key 格式
    return (
      <EduPageBlock
        resultList={questionInfo?.[0]?.ResultList || []}
        onSelect={onSelect}
        selectRect={selectRect}
      />
    );
  }

  // 多页：每页一段，附"第 N 页"标题
  return (
    <>
      {questionInfo.map((pageInfo, pageIdx) => {
        const pageNum = pageIdx + 1;
        const pagePrefix = `_p${pageNum}_`;
        return (
          <div className="edu_page_section" key={pageIdx}>
            <div className="edu_page_section__title">第 {pageNum} 页</div>
            <EduPageBlock
              resultList={pageInfo?.ResultList || []}
              onSelect={onSelect}
              selectRect={selectRect}
              pagePrefix={pagePrefix}
              pageNum={pageNum}
            />
          </div>
        );
      })}
    </>
  );
}

export default EduRenderer;
